/**
 * Reusable dense data grid (CF-0029, CF-0032, CF-0035): server-side pagination/sorting/quick filter,
 * multi-select bulk actions, column chooser persisted per view key, CSV/XLSX export, saved views.
 */
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { Box, Button, Menu, MenuItem, Stack, TextField, Toolbar, Typography } from "@mui/material";
import {
  DataGrid, type DataGridProps, type GridColDef, type GridColumnVisibilityModel, type GridPaginationModel,
  type GridRowId, type GridRowSelectionModel, type GridSortModel,
} from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { getActiveOrganization } from "../api/client";
import { SavedViewsMenu, type ViewConfig } from "./SavedViewsMenu";

export type ListParams = { page: number; page_size: number; ordering?: string; search?: string } & Record<string, unknown>;
export type ListResult<T> = { count: number; results: T[] };

export type BulkAction<T> = {
  key: string;
  label: string;
  permission?: string;
  onClick: (rows: T[]) => void | Promise<void>;
  destructive?: boolean;
};

export type DataTableProps<T extends { id: GridRowId }> = {
  viewKey: string;
  columns: GridColDef<T>[];
  data: ListResult<T> | undefined;
  isLoading: boolean;
  params: ListParams;
  onParamsChange: (next: ListParams) => void;
  exportPath?: string;            // e.g. "/api/v1/sales/orders/export/"
  bulkActions?: BulkAction<T>[];
  toolbarExtras?: ReactNode;
  onRowClick?: (row: T) => void;
  filters?: ReactNode;            // extra filter controls rendered in the toolbar
} & Omit<DataGridProps<T>, "rows" | "columns" | "paginationModel" | "onPaginationModelChange" | "sortModel" | "onSortModelChange" | "onRowClick">;

function storageKey(viewKey: string) {
  return `cf.columns.${getActiveOrganization() ?? "-"}.${viewKey}`;
}

export function DataTable<T extends { id: GridRowId }>({
  viewKey, columns, data, isLoading, params, onParamsChange, exportPath, bulkActions = [], toolbarExtras, onRowClick,
  filters, ...gridProps
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey(viewKey)) ?? "{}"); } catch { return {}; }
  });
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [searchDraft, setSearchDraft] = useState(String(params.search ?? ""));

  useEffect(() => {
    try { localStorage.setItem(storageKey(viewKey), JSON.stringify(visibility)); } catch { /* ignore */ }
  }, [visibility, viewKey]);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onParamsChangeRef = useRef(onParamsChange);
  onParamsChangeRef.current = onParamsChange;
  useEffect(() => {
    const handle = setTimeout(() => {
      const current = paramsRef.current;
      if ((current.search ?? "") !== searchDraft) onParamsChangeRef.current({ ...current, search: searchDraft, page: 1 });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchDraft]);

  const paginationModel: GridPaginationModel = { page: params.page - 1, pageSize: params.page_size };
  const sortModel: GridSortModel = useMemo(() => {
    if (!params.ordering) return [];
    const desc = params.ordering.startsWith("-");
    return [{ field: desc ? params.ordering.slice(1) : params.ordering, sort: desc ? "desc" : "asc" }];
  }, [params.ordering]);

  const selectedRows = useMemo(() => {
    const ids = selection.type === "include" ? selection.ids : new Set((data?.results ?? []).map((r) => r.id).filter((id) => !selection.ids.has(id)));
    return (data?.results ?? []).filter((r) => ids.has(r.id));
  }, [selection, data]);

  const applyView = useCallback((config: ViewConfig) => {
    if (config.columns) setVisibility(config.columns);
    onParamsChange({ ...params, ...(config.params ?? {}), page: 1 });
  }, [onParamsChange, params]);

  const currentView = (): ViewConfig => {
    const rest: Record<string, unknown> = { ...params };
    delete rest.page;
    delete rest.page_size;
    return { params: rest, columns: visibility };
  };

  const exportUrl = (file: "csv" | "xlsx") => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "" && !["page", "page_size"].includes(k)) q.set(k, String(v));
    });
    q.set("file", file);
    return `${exportPath}?${q.toString()}`;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Toolbar disableGutters variant="dense" sx={{ gap: 1, flexWrap: "wrap", mb: 1 }}>
        <TextField
          size="small" placeholder={t("common.search")} value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)}
          sx={{ width: 260 }} inputProps={{ "aria-label": t("common.search") }}
        />
        {filters}
        <Box sx={{ flexGrow: 1 }} />
        {selectedRows.length > 0 && bulkActions.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">{t("common.selected", { count: selectedRows.length })}</Typography>
            {bulkActions.map((a) => (
              <Button key={a.key} color={a.destructive ? "error" : "primary"} variant="outlined"
                onClick={() => void a.onClick(selectedRows)}>{a.label}</Button>
            ))}
          </Stack>
        )}
        {toolbarExtras}
        <SavedViewsMenu viewKey={viewKey} current={currentView} onApply={applyView} />
        {exportPath && (
          <>
            <Button startIcon={<DownloadIcon />} onClick={(e) => setExportAnchor(e.currentTarget)}>{t("common.export")}</Button>
            <Menu open={!!exportAnchor} anchorEl={exportAnchor} onClose={() => setExportAnchor(null)}>
              <MenuItem component="a" href={exportUrl("csv")} onClick={() => setExportAnchor(null)}>CSV</MenuItem>
              <MenuItem component="a" href={exportUrl("xlsx")} onClick={() => setExportAnchor(null)}>Excel</MenuItem>
            </Menu>
          </>
        )}
        <Button startIcon={<PrintIcon />} onClick={() => window.print()}>Print</Button>
      </Toolbar>
      <DataGrid<T>
        density="compact"
        rows={data?.results ?? []}
        rowCount={data?.count ?? 0}
        columns={columns}
        loading={isLoading}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[25, 50, 100, 250]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => onParamsChange({ ...params, page: m.page + 1, page_size: m.pageSize })}
        sortModel={sortModel}
        onSortModelChange={(m) => onParamsChange({ ...params, page: 1, ordering: m[0] ? `${m[0].sort === "desc" ? "-" : ""}${m[0].field}` : undefined })}
        checkboxSelection={bulkActions.length > 0}
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={setVisibility}
        onRowClick={onRowClick ? (p) => onRowClick(p.row as T) : undefined}
        disableRowSelectionOnClick
        showToolbar
        autoHeight
        sx={{ bgcolor: "background.paper", "& .MuiDataGrid-row": { cursor: onRowClick ? "pointer" : "default" } }}
        {...gridProps}
      />
    </Box>
  );
}

export function useListParams(initial: Partial<ListParams> = {}) {
  const [params, setParams] = useState<ListParams>({ page: 1, page_size: 50, ...initial });
  return [params, setParams] as const;
}
